// 警報處理按鈕（EVT-06）：〔已看到〕〔我來處理〕〔已處理〕〔誤報〕+ 一行備註；結案的只有〔重開〕。
// 狀態全家戶 / 場域共用：別人先改了 → 409，提示「已被 ○○ 處理」並以伺服器的目前狀態重畫。guest 只看到狀態、沒有按鈕。
import { useRef, useState } from 'react';
import { useDismiss } from './useDismiss';
import { useTranslation } from 'react-i18next';
import { useApp } from '../lib/AppContext';
import { useAlarmStates } from '../lib/alarmStates';
import { ALARM_NOTE_MAX, actionsFor, alarmApi, conflictOf, noteOK, type AlarmInfo, type AlarmStateName } from '../lib/alarms';

interface Props {
  homeId: string;
  eventId: string;
  /** 清單 / 彈窗用：備註欄收起來，按〔備註〕才出現 */
  compact?: boolean;
  /**
   * 事件列表的警報列（VMS UX 評審 2026-10-13 P1-3 ④、SPEC EVT-02 列上的動作）：列上只放狀態膠囊與一顆〔處理 ▾〕，
   * 已看到 / 我來處理 / 已處理 / 誤報 / 加備註收在選單裡，不在每列攤開 6–8 顆按鈕。
   */
  menu?: boolean;
  /**
   * 警報卡用（第七輪 R7-P0-1、B7-01：6 顆按鈕排成 4 行、蓋住頁面）：一行——〔已看到〕〔我來處理〕這類主要動作攤開，
   * 誤報 / 備註 / 其他收進〔⋯〕。狀態膠囊只在不是「還沒處理」時才出。
   */
  popup?: boolean;
  /** 改完（含 409 重畫）之後 */
  onChanged?(info: AlarmInfo): void;
  /** 已知的狀態（事件詳細從 GET 拿到的）；共用快取有更新的就用快取的 */
  initial?: AlarmInfo;
  /**
   * 合併成一列的同一波警報（同一台＋同一種，第四輪 R4-P1-2、B4-08）：處理這列時一起套用到這些事件。
   * 每則各自讀狀態、各自帶版本；已經被別人處理掉（狀態不同 / 409）的跳過，不擋其他則。
   */
  alsoIds?: string[];
  /**
   * 已恢復的狀態類警報（影像訊號中斷，SPEC #vms-web-alarm-tidy）：還沒處理時〔處理 ▾〕旁多一顆〔已恢復，結案〕＝標「已處理」，
   * 合併列套用整波（alsoIds）。不自動帶備註。
   */
  recovered?: boolean;
}

export function AlarmStatus({ info }: { info: AlarmInfo | undefined }) {
  const { t } = useTranslation();
  const { session } = useApp();
  const state = info?.state ?? 'open';
  const me = session?.user?.user_id ?? '';
  const label =
    state === 'claimed' && info?.claimed_by
      ? info.claimed_by === me
        ? t('alarm.claimed_by_me')
        : t('alarm.claimed_by', { name: info.claimed_by_name || t('alarm.someone') })
      : t(`alarm.state_${state}`);
  return (
    <span className={`badge alarm-state alarm-${state}`} data-alarm-state={state} title={info?.note || undefined}>
      {label}
    </span>
  );
}

export function AlarmActions({ homeId, eventId, compact, menu, popup, onChanged, initial, alsoIds, recovered }: Props) {
  const { t } = useTranslation();
  const { session } = useApp();
  const me = session?.user?.user_id ?? '';
  const alarms = useAlarmStates();
  const shared = alarms.infoOf(eventId);
  const info: AlarmInfo = shared ?? initial ?? { state: 'open' };
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(!compact && !menu);
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLSpanElement>(null);
  useDismiss(open, () => setOpen(false), menuRef); // Esc、點外面關（第二輪 B2-03）
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'warn' | 'error'; text: string } | null>(null);
  const api = alarms.apiOf(homeId);
  const can = alarms.canHandle(homeId);

  /** 同一波的其他則：只動跟這列原本同一個狀態的（別人已經處理掉的不碰），回傳成功幾則。 */
  async function applyToOthers(to: AlarmStateName, from: AlarmStateName): Promise<number> {
    let ok = 0;
    for (const id of alsoIds ?? []) {
      try {
        let cur = alarms.infoOf(id);
        if (!cur || cur.version === undefined) cur = (await alarmApi.get(api!, homeId, id)).alarm;
        if (cur.state !== from) continue;
        const r = await alarmApi.setState(api!, homeId, id, { state: to, expected_state: cur.state, expected_version: cur.version ?? -1, note: note.trim() || undefined });
        alarms.set(homeId, id, r.alarm);
        ok++;
      } catch (e) {
        const c = conflictOf(e);
        if (c) alarms.set(homeId, id, c.current);
      }
    }
    return ok;
  }

  async function act(to: AlarmStateName) {
    if (!api || busy) return;
    if (!noteOK(note)) {
      setMsg({ kind: 'error', text: t('alarm.note_invalid', { max: ALARM_NOTE_MAX }) });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      // 畫面上的狀態沒有版本（還沒進最近清單、也沒收過串流幀）→ 先讀這筆，不猜版本（plan#36）
      let cur = info;
      if (cur.version === undefined) {
        cur = (await alarmApi.get(api, homeId, eventId)).alarm;
        if (cur.state !== info.state) {
          alarms.set(homeId, eventId, cur);
          setMsg({ kind: 'warn', text: t('alarm.conflict_anon') });
          onChanged?.(cur);
          return;
        }
      }
      const r = await alarmApi.setState(api, homeId, eventId, {
        state: to,
        expected_state: cur.state,
        expected_version: cur.version ?? -1, // 伺服器一定帶 version；真的沒有就送 -1 讓伺服器拒（400），不猜
        note: note.trim() || undefined,
      });
      alarms.set(homeId, eventId, r.alarm);
      if (alsoIds?.length) {
        const n = await applyToOthers(to, cur.state);
        if (n > 0) setMsg({ kind: 'warn', text: t('alarm.also_done', { n: n + 1 }) });
      }
      setNote('');
      if (compact || menu) setShowNote(false);
      setOpen(false);
      onChanged?.(r.alarm);
    } catch (e) {
      const c = conflictOf(e);
      if (c) {
        alarms.set(homeId, eventId, c.current);
        setMsg({ kind: 'warn', text: c.byName ? t('alarm.conflict', { name: c.byName }) : t('alarm.conflict_anon') });
        onChanged?.(c.current);
      } else {
        setMsg({ kind: 'error', text: t('alarm.failed', { msg: e instanceof Error ? e.message : String(e) }) });
      }
    } finally {
      setBusy(false);
    }
  }

  const msgView = msg && (
    <span className={msg.kind === 'warn' ? 'warn-text small' : 'error small'} role="status" data-alarm-msg={msg.kind}>
      {msg.text}
    </span>
  );
  if (menu || popup) {
    const all = actionsFor(info, me);
    // 警報卡：主要動作（不是誤報的前兩個）攤開，其餘收進〔⋯〕
    const inline = popup ? all.filter((a) => a !== 'false_alarm').slice(0, 2) : [];
    const acts = all.filter((a) => !(inline as AlarmStateName[]).includes(a));
    return (
      <div className={'alarm-actions' + (popup ? ' alarm-actions-popup' : '')} data-alarm-actions={eventId} onClick={(e) => e.stopPropagation()}>
        {(!popup || info.state !== 'open') && <AlarmStatus info={info} />}
        {can && recovered && menu && (info.state === 'open' || info.state === 'acknowledged') && (
          <button
            type="button"
            className="btn btn-sm"
            disabled={busy || !api}
            data-alarm-action="resolve-recovered"
            title={t('alarm.resolve_recovered_tip')}
            onClick={() => void act('resolved')}
          >
            {t('alarm.act_resolve_recovered')}
          </button>
        )}
        {can &&
          inline.map((to) => (
            <button key={to} type="button" className="btn btn-sm btn-primary" disabled={busy || !api} data-alarm-action={to} onClick={() => void act(to)}>
              {to === 'claimed' && info.state === 'claimed' ? t('alarm.act_take_over') : t(`alarm.act_${to}`)}
            </button>
          ))}
        {can && (acts.length > 0 || popup) && (
          <span className="ot-menu-wrap" ref={menuRef}>
            <button
              type="button"
              className={popup ? 'btn btn-sm' : 'btn btn-sm btn-primary'}
              aria-haspopup="menu"
              aria-expanded={open}
              aria-label={popup ? t('alarm.more_actions') : undefined}
              title={popup ? t('alarm.more_actions') : undefined}
              data-alarm-menu
              onClick={() => setOpen((o) => !o)}
              disabled={!api}
            >
              {popup ? '⋯' : `${t('alarm.handle_menu')} ▾`}
            </button>
            {open && (
              <span className={'ot-menu alarm-menu' + (popup ? ' alarm-menu-up' : '')} role="menu">
                {acts.map((to) => (
                  <button key={to} type="button" role="menuitem" disabled={busy} data-alarm-action={to} onClick={() => void act(to)}>
                    {to === 'claimed' && info.state === 'claimed' ? t('alarm.act_take_over') : t(`alarm.act_${to}`)}
                  </button>
                ))}
                {!showNote ? (
                  <button type="button" role="menuitem" data-alarm-note-toggle onClick={() => setShowNote(true)}>
                    {t('alarm.note_add')}
                  </button>
                ) : (
                  <input
                    className="input input-sm alarm-note"
                    data-alarm-note
                    autoFocus
                    value={note}
                    maxLength={ALARM_NOTE_MAX}
                    placeholder={t('alarm.note_placeholder', { max: ALARM_NOTE_MAX })}
                    aria-label={t('alarm.note_label')}
                    onChange={(e) => setNote(e.target.value.replace(/[\r\n]/g, ' '))}
                  />
                )}
              </span>
            )}
          </span>
        )}
        {msgView}
      </div>
    );
  }
  return (
    <div className="alarm-actions" data-alarm-actions={eventId} onClick={(e) => e.stopPropagation()}>
      <AlarmStatus info={info} />
      {can &&
        actionsFor(info, me).map((to) => (
          <button key={to} type="button" className={`btn btn-sm${to === 'false_alarm' ? '' : ' btn-primary'}`} disabled={busy || !api} data-alarm-action={to} onClick={() => void act(to)}>
            {to === 'claimed' && info.state === 'claimed' ? t('alarm.act_take_over') : t(`alarm.act_${to}`)}
          </button>
        ))}
      {can && compact && !showNote && (
        <button type="button" className="btn btn-sm" data-alarm-note-toggle onClick={() => setShowNote(true)}>
          {t('alarm.note_btn')}
        </button>
      )}
      {can && showNote && (
        <input
          className="input input-sm alarm-note"
          data-alarm-note
          value={note}
          maxLength={ALARM_NOTE_MAX}
          placeholder={t('alarm.note_placeholder', { max: ALARM_NOTE_MAX })}
          aria-label={t('alarm.note_label')}
          onChange={(e) => setNote(e.target.value.replace(/[\r\n]/g, ' '))}
        />
      )}
      {!compact && info.note && (
        <span className="muted small" data-alarm-last-note>
          {t('alarm.last_note', { note: info.note })}
        </span>
      )}
      {msg && (
        <span className={msg.kind === 'warn' ? 'warn-text small' : 'error small'} role="status" data-alarm-msg={msg.kind}>
          {msg.text}
        </span>
      )}
    </div>
  );
}
